import { FilterPipe } from "./../../../pipes/filter.pipe";
import { CommonModule, KeyValue, ViewportScroller } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    ViewChild,
    WritableSignal,
    effect,
    inject,
    signal,
} from "@angular/core";
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { ConversionType } from "@playcharge/common/models/backend/offers/conversion-type";
import { CampaignOfferGoalType } from "@playcharge/common/models/backend/offers/campaign-offer-goal-type";
import { BackOfficeOfferViewModel } from "@playcharge/common/models/backend/offers/backoffice/backoffice-offer-view-model";
import { EnumKeyValuePipe } from "../../../pipes/enumKeyValue.pipe";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { MatExpansionModule, MatExpansionPanel } from "@angular/material/expansion";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { MatIconModule } from "@angular/material/icon";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatButton } from "@angular/material/button";
import {
    CountryRewardForm,
    OfferGoalForm,
    OfferGoalsForm,
} from "../../../models/offers/offer-goal-forms";
import { MatTabsModule } from "@angular/material/tabs";
import { OfferGoalComponent } from "../offer-goal/offer-goal.component";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { AddGoalDialogComponent } from "../../dialogs/add-goal-dialog/add-goal-dialog.component";
import { OfferGoalsTab } from "../../../models/offers/offer-goals-tab";
import { UpdateOfferGoalsEditModel } from "@playcharge/common/models/backend/offers/backoffice/update-offer-goals-edit-model";
import { Nullable } from "@playcharge/common/models/backend/nullable";
import { ToastrService } from "ngx-toastr";
import { OfferStore } from "../../../stores/offers/offer.store";
import { CountryReward } from "@playcharge/common/models/backend/offers/country-reward";
import { BackOfficeOfferGoalViewModel } from "@playcharge/common/models/backend/offers/backoffice/backoffice-offer-goal-view-model";

@Component({
    selector: "app-offer-goals",
    standalone: true,
    templateUrl: "./offer-goals.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [OfferStore],
    imports: [
        CommonModule,
        MatTabsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatExpansionModule,
        MatCheckboxModule,
        MatChipsModule,
        MatIconModule,
        MatDatepickerModule,
        MatButton,
        FilterPipe,
        OfferGoalComponent,
        MatDialogModule,
    ],
})
export class OfferGoalsComponent {
    public vpScroll: ViewportScroller = inject(ViewportScroller);

    private _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
    private _matDialog: MatDialog = inject(MatDialog);
    private _toastrService: ToastrService = inject(ToastrService);
    public offerStore: OfferStore = inject(OfferStore);

    public form: FormGroup<OfferGoalsForm> = new FormGroup<OfferGoalsForm>({
        goals: new FormArray<FormGroup<OfferGoalForm>>([]),
    });

    public tabs: WritableSignal<KeyValue<CampaignOfferGoalType, OfferGoalsTab>[]> = signal<
        KeyValue<CampaignOfferGoalType, OfferGoalsTab>[]
    >([
        { key: CampaignOfferGoalType.Event, value: { label: "Events" } },
        {
            key: CampaignOfferGoalType.LevelUp,
            value: { label: "Level Ups" },
        },
        {
            key: CampaignOfferGoalType.LimitedOffer,
            value: { label: "Limited Offers" },
        },
        {
            key: CampaignOfferGoalType.AdRevenueCashback,
            value: { label: "Ad Revenue Cashback" },
        },
        {
            key: CampaignOfferGoalType.DailyLogin,
            value: { label: "Daily Login" },
        },
        {
            key: CampaignOfferGoalType.DailyLoginCounter,
            value: { label: "Daily Login Counter" },
        },
        {
            key: CampaignOfferGoalType.AccumulatedRevenue,
            value: { label: "Accumulated Revenue" },
        },
    ]);

    public activeTabIndex: WritableSignal<number> = signal<number>(0);

    public ConversionType: typeof ConversionType = ConversionType;
    public CampaignOfferGoalType: typeof CampaignOfferGoalType = CampaignOfferGoalType;

    constructor() {
        effect(() => {
            const offer: BackOfficeOfferViewModel | null = this.offerStore.offer();

            if (offer && !this.goalsArray.length) {
                for (const goal of offer.goals?.sort((a, b) => b.order - a.order) ?? []) {
                    this.addGoal(goal);
                }

                this._changeDetectorRef.detectChanges();
            }
        });
    }

    public openAddGoalDialog(): void {
        this._matDialog
            .open(AddGoalDialogComponent)
            .afterClosed()
            .subscribe((goalType: Nullable<CampaignOfferGoalType>) => {
                if (goalType) {
                    this.addGoal({ goalType: goalType } as BackOfficeOfferGoalViewModel, true);
                }
            });
    }

    public addGoal(goal?: BackOfficeOfferGoalViewModel, withTabChange?: boolean): void {
        this.form.controls.goals.push(
            new FormGroup<OfferGoalForm>({
                id: new FormControl<number>(goal?.id ?? 0, { nonNullable: true }),
                conversionType: new FormControl<ConversionType>(
                    goal?.conversionType ?? ConversionType.None,
                    { nonNullable: true },
                ),
                billingPrice: new FormControl<number>(goal?.billingPrice ?? 0, {
                    nonNullable: true,
                }),
                reward: new FormControl<number>(goal?.reward ?? 0, { nonNullable: true }),
                expReward: new FormControl<number>(goal?.expReward ?? 0, { nonNullable: true }),
                rewardsByCountries: new FormArray<FormGroup<CountryRewardForm>>(
                    goal?.rewardsByCountries
                        ? this.mapToCountryRewardsForm(goal.rewardsByCountries)
                        : [],
                ),
                cashbackRewardRate: new FormControl<number>(goal?.cashbackRewardRate ?? 0, { nonNullable: true }),
                goalType: new FormControl<CampaignOfferGoalType>(
                    goal?.goalType ?? CampaignOfferGoalType.None,
                    { nonNullable: true },
                ),
                maxEventsPerDay: new FormControl<number>(goal?.maxEventsPerDay ?? 0, {
                    nonNullable: true,
                }),
                maxTotalEvents: new FormControl<number>(goal?.maxTotalEvents ?? 0, {
                    nonNullable: true,
                }),
                levelUpValues: new FormControl<Nullable<string[]>>(goal?.levelUpValues),
                iapValue: new FormControl<number>(goal?.targetPurchaseAmount ?? 0, {
                    nonNullable: true,
                }),
                iapCount: new FormControl<number>(goal?.targetPurchaseCount ?? 0, {
                    nonNullable: true,
                }),
                dailyLoginDays: new FormControl<number>(goal?.dailyLoginDays ?? 0, {
                    nonNullable: true,
                }),
                startDate: new FormControl<Nullable<string>>(goal?.startDate),
                endDate: new FormControl<Nullable<string>>(goal?.endDate),
                offsetStartDate: new FormControl<Nullable<number>>(goal?.offsetStartDate),
                offsetDuration: new FormControl<Nullable<string>>(goal?.offsetDuration),
                eventName: new FormControl<Nullable<string>>(goal?.eventName),
                eventTemplateName: new FormControl<Nullable<string>>(goal?.eventTemplateName),
                order: new FormControl<number>(goal?.order ?? 0, { nonNullable: true }),
                description: new FormControl<Nullable<string>>(goal?.description),
                affiseGoal: new FormControl<Nullable<string>>(goal?.affiseGoal),
                antiFraudTimeout: new FormControl<Nullable<number>>(goal?.antiFraudTimeout),
                isActive: new FormControl<boolean>(goal?.isActive ?? false, { nonNullable: true }),
                isExtra: new FormControl<boolean>(goal?.isExtra ?? false, { nonNullable: true }),
                challengeBlockId: new FormControl<Nullable<number>>(goal?.challengeBlockId),
            }),
        );

        if (withTabChange) {
            this._changeDetectorRef.detectChanges();

            this.activeTabIndex.set(this.tabs().findIndex((x) => x.key === goal?.goalType));
        }

        if (!goal?.id) {
            setTimeout(() => {
                this.vpScroll.scrollToAnchor("scroll");
            }, 100);
        }
    }

    public removeGoal(formGroup: FormGroup<OfferGoalForm>): void {
        const index: number = this.goalsArray.controls.indexOf(formGroup);

        if (index !== -1) {
            this.goalsArray.removeAt(index);
        }
    }

    public get goalsArray(): FormArray<FormGroup<OfferGoalForm>> {
        return this.form.controls.goals;
    }

    public filterByGoalType(goalType: CampaignOfferGoalType) {
        return (goal: FormGroup<OfferGoalForm>) => goal.value.goalType === goalType;
    }

    public updateGoals(): void {
        const updateModel: UpdateOfferGoalsEditModel = {
            goals: this.form.getRawValue().goals,
        };

        this.offerStore.updateOfferGoals(updateModel).subscribe({
            next: () => {
                this._toastrService.success("Successfully updated");
            },
            error: (error) => {
                this._toastrService.error(error?.error?.description ?? "Internal Error");
            },
        });
    }

    private mapToCountryRewardsForm(
        countryRewards: CountryReward[],
    ): FormGroup<CountryRewardForm>[] {
        return countryRewards.map(
            (x) =>
                new FormGroup<CountryRewardForm>({
                    countryCode: new FormControl<string>(x.countryCode, {
                        nonNullable: true,
                        validators: [Validators.required],
                    }),
                    reward: new FormControl<number>(x.reward, { nonNullable: true }),
                }),
        );
    }
}
